import { apiFetch, ApiError } from '../apiFetch'
import type {
  ProductionOrder,
  ProductionOrderPatchInput,
  ProductionMonthLock,
  ProductionMonthHistoryItem,
  WorkUnit,
  WorkUnitKind,
  WorkUnitStatus,
} from './types'

async function parseResponse<T>(res: Response, fallback: string): Promise<T> {
  const data = await res.json().catch(() => null)
  if (!res.ok) {
    const err = data?.error
    const code = err?.code ?? 'HTTP_ERROR'
    const message = err?.message ?? `${fallback} (${res.status})`
    const details = err?.details
    throw new ApiError(code, message, res.status, details)
  }
  return data as T
}

export interface ProductionOrderFilter {
  month?: string
  q?: string
  customerId?: string
  status?: string
}

export async function listProductionOrdersApi(
  filter?: ProductionOrderFilter,
): Promise<ProductionOrder[]> {
  const params = new URLSearchParams()
  if (filter?.month) params.set('month', filter.month)
  if (filter?.q) params.set('q', filter.q)
  if (filter?.customerId) params.set('customerId', filter.customerId)
  if (filter?.status && filter.status !== 'all') params.set('status', filter.status)

  const query = params.toString() ? `?${params.toString()}` : ''
  const res = await apiFetch(`/api/production-orders${query}`)
  const body = await parseResponse<{ ok: true; production_orders: ProductionOrder[] }>(
    res,
    'Không thể tải danh sách lệnh sản xuất',
  )
  return body.production_orders
}

export async function getProductionOrderByIdApi(id: string): Promise<ProductionOrder> {
  const res = await apiFetch(`/api/production-orders/${encodeURIComponent(id)}`)
  const body = await parseResponse<{ ok: true; production_order: ProductionOrder }>(
    res,
    'Không thể tải thông tin lệnh sản xuất',
  )
  return body.production_order
}

export async function patchProductionOrderApi(
  id: string,
  patch: ProductionOrderPatchInput,
): Promise<ProductionOrder> {
  const res = await apiFetch(`/api/production-orders/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  const body = await parseResponse<{ ok: true; production_order: ProductionOrder }>(
    res,
    'Không thể cập nhật dòng kế hoạch',
  )
  return body.production_order
}

export async function reorderProductionOrdersApi(
  ids: string[],
  sortByCompletion: boolean,
): Promise<{ order: string[]; moved: string[] }> {
  const res = await apiFetch('/api/production-orders/order', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, sort_by_completion: sortByCompletion }),
  })
  // Backend trả { ok, order, moved } (server/src/services/planning/productionOrderService.ts
  // reorder() -> return { order: finalOrder, moved }), KHÔNG phải { ids, moved_ids }.
  return parseResponse<{ ok: true; order: string[]; moved: string[] }>(
    res,
    'Không thể lưu thứ tự kế hoạch',
  )
}

export async function ackProductionOrderChangeApi(id: string): Promise<ProductionOrder> {
  const res = await apiFetch(`/api/production-orders/${encodeURIComponent(id)}/ack-change`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  })
  const body = await parseResponse<{ ok: true; production_order: ProductionOrder }>(
    res,
    'Không thể xác nhận thay đổi đơn hàng',
  )
  return body.production_order
}

export async function listProductionMonthsApi(): Promise<ProductionMonthLock[]> {
  const res = await apiFetch('/api/production-months')
  const body = await parseResponse<{ ok: true; production_months: ProductionMonthLock[] }>(
    res,
    'Không thể tải danh sách tháng sản xuất',
  )
  return body.production_months
}

export async function approveProductionMonthApi(month: string): Promise<ProductionMonthLock> {
  const res = await apiFetch(`/api/production-months/${encodeURIComponent(month)}/approve`, {
    method: 'POST',
  })
  const body = await parseResponse<{ ok: true; production_month: ProductionMonthLock }>(
    res,
    'Không thể duyệt tháng kế hoạch',
  )
  return body.production_month
}

export async function reopenProductionMonthApi(
  month: string,
  reason: string,
): Promise<ProductionMonthLock> {
  const res = await apiFetch(`/api/production-months/${encodeURIComponent(month)}/reopen`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  })
  const body = await parseResponse<{ ok: true; production_month: ProductionMonthLock }>(
    res,
    'Không thể mở lại tháng kế hoạch',
  )
  return body.production_month
}

export async function getProductionMonthHistoryApi(
  month: string,
): Promise<ProductionMonthHistoryItem[]> {
  const res = await apiFetch(`/api/production-months/${encodeURIComponent(month)}/history`)
  const body = await parseResponse<{ ok: true; history: ProductionMonthHistoryItem[] }>(
    res,
    'Không thể tải lịch sử tháng kế hoạch',
  )
  return body.history
}

export interface WorkUnitFilter {
  kind?: WorkUnitKind
  status?: WorkUnitStatus
  q?: string
}

export async function listWorkUnitsApi(filter?: WorkUnitFilter): Promise<WorkUnit[]> {
  const params = new URLSearchParams()
  if (filter?.kind) params.set('kind', filter.kind)
  if (filter?.status) params.set('status', filter.status)
  if (filter?.q) params.set('q', filter.q)

  const query = params.toString() ? `?${params.toString()}` : ''
  const res = await apiFetch(`/api/work-units${query}`)
  const body = await parseResponse<{ ok: true; work_units: WorkUnit[] }>(
    res,
    'Không thể tải danh sách đơn vị sản xuất',
  )
  return body.work_units
}

export async function createWorkUnitApi(input: {
  code: string
  name: string
  kind: WorkUnitKind
  status?: WorkUnitStatus
  note?: string
}): Promise<WorkUnit> {
  const res = await apiFetch('/api/work-units', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  const body = await parseResponse<{ ok: true; work_unit: WorkUnit }>(
    res,
    'Không thể tạo đơn vị sản xuất',
  )
  return body.work_unit
}

export async function patchWorkUnitApi(
  id: string,
  patch: { name?: string; status?: WorkUnitStatus; note?: string },
): Promise<WorkUnit> {
  const res = await apiFetch(`/api/work-units/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  const body = await parseResponse<{ ok: true; work_unit: WorkUnit }>(
    res,
    'Không thể cập nhật đơn vị sản xuất',
  )
  return body.work_unit
}

export async function deleteWorkUnitApi(id: string): Promise<void> {
  const res = await apiFetch(`/api/work-units/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
  await parseResponse<{ ok: true }>(res, 'Không thể xóa đơn vị sản xuất')
}

// Aliases for convenience across UI components
export const fetchProductionOrders = listProductionOrdersApi
export const fetchProductionOrderById = getProductionOrderByIdApi
export const updateProductionOrder = patchProductionOrderApi
export const updateProductionOrderPositions = reorderProductionOrdersApi
export const ackProductionOrderChange = ackProductionOrderChangeApi
export const approveProductionMonth = approveProductionMonthApi
export const reopenProductionMonth = reopenProductionMonthApi
export const fetchWorkUnits = listWorkUnitsApi
export const fetchProductionMonthHistory = getProductionMonthHistoryApi

export async function fetchProductionMonthLock(month: string): Promise<ProductionMonthLock | null> {
  const months = await listProductionMonthsApi()
  return months.find((m) => m.month === month) ?? null
}

export async function patchProductionOrderStatus(
  id: string,
  status: string,
  reason?: string,
): Promise<ProductionOrder> {
  return patchProductionOrderApi(id, {
    note: reason ? `[${status.toUpperCase()}] ${reason}` : undefined,
  })
}

